import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

const PAGE_SIZE = 12;

interface Customer {
    _id: string;
    name: string;
    phone: string;
    email?: string;
    picture?: string;
    birthDate?: string;
    gender?: string;
    redeemCode?: string;
    earnCode?: string;
    coffeeBeans: number;
    totalEarnedCoffeeBeans: number;
    tier?: {
        _id: string;
        name: string | { lang: string; value: string }[];
    };
    feedbacksCount: number;
    ordersCount: number;
}

export default function CustomersScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const [page, setPage] = useState(1);

    // ── Customers fetch ─────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("customers", { query: { page: 1, limit: PAGE_SIZE } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: PAGE_SIZE } });
    }, [page]);

    const customers: Customer[] = data?.data?.customers ?? [];
    const total: number = data?.data?.total ?? 0;

    // ── Helper Functions ────────────────────────────────────────────────────────
    const getTierName = (tier?: { name: string | { lang: string; value: string }[] }): string => {
        if (!tier?.name) return "—";
        if (typeof tier.name === "string") return tier.name;
        if (Array.isArray(tier.name)) {
            const match = tier.name.find((n) => n.lang === lang) ?? tier.name[0];
            return match?.value ?? "—";
        }
        return "—";
    };

    const formatBirthDate = (dateString?: string): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    // ── Columns ─────────────────────────────────────────────────────────────────
    const columns: TableColumn<Customer>[] = [
        {
            key: "picture",
            header: t.customers.col.picture,
            width: "80px",
            render: (row) => (
                <div className="customers-picture-wrap">
                    {row.picture ? (
                        <img src={row.picture} alt={row.name} className="customers-picture" />
                    ) : (
                        <div className="customers-picture-placeholder">{row.name?.charAt(0) ?? "?"}</div>
                    )}
                </div>
            ),
        },
        {
            key: "name",
            header: t.customers.col.name,
            width: "180px",
            render: (row) => <TableCell.Text value={row.name || "—"} maxChars={25} />,
        },
        {
            key: "phone",
            header: t.customers.col.phone,
            width: "180px",
            render: (row) => <TableCell.Text value={row.phone || "—"} />,
        },
        {
            key: "email",
            header: t.customers.col.email,
            width: "220px",
            render: (row) => <TableCell.Text value={row.email || "—"} maxChars={25} />,
        },
        {
            key: "gender",
            header: t.customers.col.gender,
            width: "100px",
            render: (row) => <TableCell.Text value={row.gender ? t.settings[row.gender as "male" | "female"] : "—"} />,
        },
        {
            key: "birthDate",
            header: t.customers.col.birthDate,
            width: "140px",
            render: (row) => <TableCell.Text value={formatBirthDate(row.birthDate)} />,
        },
        {
            key: "coffeeBeans",
            header: t.customers.col.coffeeBeans,
            width: "180px",
            render: (row) => (
                <span className="customers-beans-badge">
                    {row.coffeeBeans.toLocaleString()} {t.customers.beans}
                </span>
            ),
        },
        {
            key: "tier",
            header: t.customers.col.tier,
            width: "140px",
            render: (row) => <TableCell.Text value={getTierName(row.tier)} maxChars={20} />,
        },
        {
            key: "orders",
            header: t.customers.col.orders,
            width: "100px",
            render: (row) => <span className="customers-count-text">{row.ordersCount}</span>,
        },
        {
            key: "feedbacks",
            header: t.customers.col.feedbacks,
            width: "100px",
            render: (row) => <span className="customers-count-text">{row.feedbacksCount}</span>,
        },
    ];

    const filterContent = <input className="actions-bar-search-input" placeholder={t.actionsBar.search} />;

    return (
        <div className="customers-screen">
            <ActionsBar filterContent={filterContent} />
            <div className="customers-screen-content">
                <Table
                    columns={columns}
                    data={customers}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
                />
            </div>
        </div>
    );
}
